import { Typography, Button, Box } from "@mui/material";
import CriSection from "../../components/CriSection";
import { useState } from "react";
import PdfDialog from "../../components/PdfDialog";
import LichenPDF from "../../assets/files/completed_research/lichen_latest.pdf";
import LichenJohnson from "../../assets/files/completed_research/LichensOfJohnson.pdf";

const Overview = () => {
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");

  const onShowPDF = ({ title, pdf }: { title: string; pdf: string }) => {
    setShowPdfDialog(true);
    setPdfTitle(title);
    setPdfUrl(pdf);
  };

  return (
    <CriSection>
      <Typography
        sx={{
          fontSize: "36px",
          fontWeight: 300,
        }}
      >
        Overview
      </Typography>

      <Typography
        sx={{
          fontWeight: 300,
          fontSize: "16px",
          lineHeight: 1.2,
          marginTop: "12px",
        }}
      >
        Gerould Wilhelm, Director of Research, continues his work on the lichens
        of the 53 counties that encompass the region around the southern end of
        Lake Michigan. This region consists of four states: Wisconsin (SE/SC),
        Illinois (NE/NC), Indiana (NW/NC), and Michigan (SW). In 1998, a flora
        of the 22-county Chicago region was presented, which indicated that the
        flora at the time was notably more depauperate than recent observations
        have shown. While the data are still being collected and analyzed, it
        seems that recent improvements in regional air quality have resulted in
        a positive response by lichenized fungi.
      </Typography>

      <Typography
        sx={{
          fontWeight: 300,
          fontSize: "16px",
          lineHeight: 1.2,
          marginTop: "12px",
          marginBottom: "6px",
        }}
      >
        As a result of rather intensive surveys over the last 25 years, many
        more lichens have been collected in the same region. Some of these
        specimens vindicate earlier reports by Calkins, who published a local
        flora in 1898, but many others reflect a more recent origin. The species
        composition of the lichen flora continues to change. This flora of the
        53-county region, a much larger area, presents 528 species of
        ascomycetous fungi, most of which are lichenized. Poorly collected are
        lichens from the central Midwest states. So, it is unclear which species
        in neighboring districts are here but unknown. For this reason, we are
        including species reported from just outside of the region to alert
        local students of their significance should they encounter them. Such
        species have been keyed out and included with short descriptions.
        Characterized are four ways of a lichen's inhabitancy in the region:
      </Typography>

      <ul>
        <li>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.2,
            }}
          >
            350 lichens are represented by at least one record in the herbarium
            at the Morton Arboretum, many collected within the past 35 years. We
            believe that several of these are yet to be described and have given
            them "herbarium names."
          </Typography>
        </li>

        <li>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.2,
            }}
          >
            Known are another 55 from other herbaria.
          </Typography>
        </li>

        <li>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.2,
            }}
          >
            Yet to be collected are another 20 lichens of reasonably reliable
            literature records.
          </Typography>
        </li>
        <li>
          <Typography
            sx={{
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.2,
            }}
          >
            Included are 101 lichen species from just outside the region to
            alert students of their presence should they discover them locally,
            making the flora more helpful to Midwestern lichenologists.
          </Typography>
        </li>
      </ul>

      <Typography
        sx={{
          fontWeight: 300,
          fontSize: "16px",
          lineHeight: 1.2,
          marginTop: "12px",
          marginBottom: "6px",
        }}
      >
        The occurrence of lichens locally is uneven, based at this point that
        field studies by lichenologists are greatly dispersed. While DuPage and
        Cook counties, each have more than 190 species documented from them, 13
        counties have fewer than 40, with an overall average of 67 records per
        county. So, there is a need for more floristic work locally.
      </Typography>

      <Box
        sx={{
          marginTop: "20px",
        }}
      >
        <Button
          onClick={() =>
            onShowPDF({ title: "Lichens of Michigan", pdf: LichenPDF })
          }
          variant="contained"
          sx={{
            marginRight: "36px",
          }}
        >
          {"Southern Lake Michigan Lichens"}
        </Button>

        <Button
          onClick={() =>
            onShowPDF({ title: "Lichens of Michigan", pdf: LichenJohnson })
          }
          variant="contained"
        >
          {"Johnson's Shut-Ins State Park Lichens"}
        </Button>
      </Box>

      <PdfDialog
        open={showPdfDialog}
        onClose={() => setShowPdfDialog(false)}
        title={pdfTitle}
        pdfUrl={pdfUrl}
      />
    </CriSection>
  );
};

export default Overview;
